import React, { useState, useEffect, useRef, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { getBackendUrl } from "../../config";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { useBusinessTimezone } from "../../context/BusinessTimezone/BusinessTimezoneContext";
import { formatMessageCalendarDay, formatMessageClock } from "../../helpers/dateTimeFormat";
import { CHANNEL_ICONS, NATIVE_CHANNELS, SEAMLESS_CHANNELS } from "../../pages/Connections/connectionChannelsCatalog";
import AppModal from "../AppModal";
import AuthField from "../Auth/AuthField";

const SessionSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
});

const initialState = {
	name: "",
	greetingMessage: "",
	farewellMessage: "",
	isDefault: false,
	isDisplay: false,
	email: "",
	emailPassword: "",
	maxAttempts: 3,
	channel: "whatsapp",
};

const CHANNEL_OPTIONS = [
	{ value: "whatsapp", label: i18n.t("whatsappModal.form.whatsapp") },
	{ value: "email", label: "E-mail" },
	{ value: "facebook", label: i18n.t("whatsappModal.form.facebook") },
	{ value: "instagram", label: i18n.t("whatsappModal.form.instagram") },
	{ value: "telegram", label: i18n.t("whatsappModal.form.telegram") },
	{ value: "whatsappcloud", label: i18n.t("whatsappModal.form.whatsappcloud") },
];

function EditSection({ title, description, children }) {
	return (
		<section className="rounded-xl border border-default-200/80 bg-white p-4 dark:border-default-700/80 dark:bg-default-900/30">
			<header className="mb-3.5">
				<h3 className="m-0 text-sm font-semibold text-default-900 dark:text-default-50">
					{title}
				</h3>
				{description && (
					<p className="mb-0 mt-1 text-xs leading-relaxed text-default-500 dark:text-default-400">
						{description}
					</p>
				)}
			</header>
			{children}
		</section>
	);
}

function ConnectionChannelBanner({ channel, hub }) {
	const meta =
		NATIVE_CHANNELS.find((item) => item.id === channel) ||
		SEAMLESS_CHANNELS.find((item) => item.oauthType === channel || item.id === channel);
	const icon = meta?.icon || CHANNEL_ICONS[channel];

	return (
		<div className="flex items-center gap-3 rounded-xl border border-primary/15 bg-primary/5 px-4 py-3 dark:border-primary/20 dark:bg-primary/10">
			{meta?.icon && (
				<div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm dark:bg-default-800">
					<img src={meta.icon} alt="" className="size-7 object-contain" />
				</div>
			)}
			<div className="min-w-0 flex-1">
				<p className="mb-0 text-xs text-default-500 dark:text-default-400">
					{i18n.t("whatsappModal.form.channel")}
				</p>
				<div className="mt-0.5 flex flex-wrap items-center gap-2">
					<p className="mb-0 text-sm font-semibold text-default-900 dark:text-default-50">
						{getChannelLabel(channel)}
					</p>
					{hub && (
						<span className="inline-flex items-center rounded-full bg-violet-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-600 dark:text-violet-400">
							{i18n.t("connections.marketplace.hubBadge")}
						</span>
					)}
				</div>
			</div>
		</div>
	);
}

function IconChevronDown() {
	return (
		<svg className="size-4 shrink-0 text-default-500 dark:text-default-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="m6 9 6 6 6-6" />
		</svg>
	);
}

function ToggleSwitch({ label, hint, tooltip, checked, onChange, className = "" }) {
	return (
		<label className={`block w-full cursor-pointer ${className}`.trim()}>
			<span className="mb-2 flex items-center gap-2 text-sm font-medium text-default-900 dark:text-default-100">
				<span>{label}</span>
				{tooltip && (
					<span
						className="inline-flex size-4 shrink-0 items-center justify-center rounded-full border border-default-300 text-[10px] font-semibold text-default-500 dark:border-default-600 dark:text-default-400"
						title={tooltip}
						aria-label={tooltip}
						tabIndex={0}
					>
						?
					</span>
				)}
			</span>
			<span className="relative inline-flex shrink-0 items-center">
				<input
					type="checkbox"
					className="peer sr-only"
					checked={checked}
					onChange={onChange}
				/>
				<span className="h-6 w-11 rounded-full bg-default-300 transition peer-checked:bg-primary dark:bg-default-700" />
				<span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition peer-checked:translate-x-5 dark:bg-default-100" />
			</span>
			{hint && (
				<span className="mt-2 block text-xs text-default-500 dark:text-default-400">
					{hint}
				</span>
			)}
		</label>
	);
}

function QueueMultiSelect({
	label,
	options,
	selectedIds,
	onChange,
	placeholder,
	emptyText,
}) {
	const [open, setOpen] = useState(false);
	const containerRef = useRef(null);

	useEffect(() => {
		if (!open) return undefined;

		const handleClickOutside = (event) => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [open]);

	const selectedOptions = options.filter((option) =>
		selectedIds.some((id) => String(id) === String(option.id))
	);

	const summary = selectedOptions.length === 0
		? placeholder
		: selectedOptions.length <= 2
			? selectedOptions.map((option) => option.name).join(", ")
			: `${selectedOptions.length} setores selecionados`;

	const handleToggle = (queueId) => {
		const exists = selectedIds.some((id) => String(id) === String(queueId));
		onChange(
			exists
				? selectedIds.filter((id) => String(id) !== String(queueId))
				: [...selectedIds, queueId]
		);
	};

	return (
		<div ref={containerRef} className="relative">
			<label className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100">
				{label}
			</label>
			<button
				type="button"
				className="form-input flex w-full items-center justify-between gap-3 text-left"
				onClick={() => setOpen((current) => !current)}
			>
				<span className={selectedOptions.length > 0 ? "text-default-900 dark:text-default-100" : "text-default-500 dark:text-default-400"}>
					{summary}
				</span>
				<IconChevronDown />
			</button>

			{selectedOptions.length > 0 && (
				<div className="mt-2 flex flex-wrap gap-2">
					{selectedOptions.map((queue) => (
						<button
							key={queue.id}
							type="button"
							className="inline-flex items-center gap-2 rounded-full border border-default-200 bg-default-100 px-3 py-1 text-xs font-medium text-default-700 transition hover:border-default-300 hover:bg-default-150 dark:border-default-700 dark:bg-default-800 dark:text-default-200 dark:hover:border-default-600"
							onClick={() => handleToggle(queue.id)}
						>
							<span
								className="size-2.5 rounded-full"
								style={{ backgroundColor: queue.color || "#3b82f6" }}
							/>
							<span>{queue.name}</span>
							<span className="text-[11px] text-default-500 dark:text-default-400">x</span>
						</button>
					))}
				</div>
			)}

			{open && (
				<div className="absolute left-0 right-0 z-30 mt-2 max-h-60 overflow-auto rounded-xl border border-default-200 bg-white p-2 shadow-lg dark:border-default-700 dark:bg-default-950">
					{options.length > 0 ? (
						options.map((queue) => {
							const checked = selectedIds.some((id) => String(id) === String(queue.id));

							return (
								<label
									key={queue.id}
									className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 hover:bg-default-100 dark:hover:bg-default-800"
								>
									<input
										type="checkbox"
										className="size-4 rounded border-default-300 text-primary focus:ring-primary"
										checked={checked}
										onChange={() => handleToggle(queue.id)}
									/>
									<span
										className="size-3 rounded-full"
										style={{ backgroundColor: queue.color || "#3b82f6" }}
									/>
									<span className="text-sm text-default-800 dark:text-default-100">
										{queue.name}
									</span>
								</label>
							);
						})
					) : (
						<p className="mb-0 px-3 py-2 text-sm text-default-500 dark:text-default-400">
							{emptyText}
						</p>
					)}
				</div>
			)}
		</div>
	);
}

function formatHubChannelLabel(item) {
	if (!item) return "";
	if (!item.name) return String(item.id);
	return item.name.charAt(0).toUpperCase() + item.name.slice(1);
}

function formatConnectionDateTime(value, timeZone) {
	if (!value) return "—";
	return `${formatMessageCalendarDay(value, timeZone)} ${formatMessageClock(value, timeZone)}`;
}

function getChannelLabel(channelValue) {
	const option = CHANNEL_OPTIONS.find((item) => item.value === channelValue);
	return option?.label || channelValue;
}

function WhatsAppModal({ open, onClose, whatsAppId, initialChannel, initialHubMode }) {
	const { reload } = useContext(WhatsAppsContext);
	const { timeZone } = useBusinessTimezone();
	const [whatsApp, setWhatsApp] = useState(initialState);
	const [queues, setQueues] = useState([]);
	const [loading, setLoading] = useState(false);
	const [passwordType, setPasswordType] = useState("password");
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [selectedAutoQueueId, setSelectedAutoQueueId] = useState(null);
	const [isHubConection, setIsHubConection] = useState(false);
	const [canal, setCanal] = useState([]);

	useEffect(() => {
		if (!open) return undefined;

		let cancelled = false;

		(async () => {
			try {
				const { data } = await api.get("/queue");
				if (!cancelled) {
					setQueues(Array.isArray(data) ? data : []);
				}
			} catch (err) {
				if (!cancelled) toastError(err);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [open]);

	useEffect(() => {
		if (!open) return undefined;

		if (!whatsAppId) {
			setLoading(false);
			setWhatsApp({
				...initialState,
				channel: initialHubMode ? "" : initialChannel || initialState.channel,
			});
			setSelectedQueueIds([]);
			setSelectedAutoQueueId(null);
			setIsHubConection(Boolean(initialHubMode));
			setCanal([]);
			return undefined;
		}

		const fetchSession = async () => {
			setLoading(true);
			try {
				const { data } = await api.get(`whatsapp/${whatsAppId}`);
				setWhatsApp({
					...initialState,
					...data,
					channel: data.channel || "whatsapp",
					maxAttempts: data.maxAttempts ?? 3,
				});

				const whatsQueueIds = data.queues?.map((queue) => queue.id);
				setSelectedQueueIds(whatsQueueIds || []);

				setSelectedAutoQueueId(data.autoQueue ?? null);
				setIsHubConection(Boolean(data.hub));
			} catch (err) {
				toastError(err);
			} finally {
				setLoading(false);
			}
		};
		fetchSession();
	}, [open, whatsAppId, initialChannel, initialHubMode]);

	const handleClickShowPassword = () => {
		setPasswordType(passwordType === "text" ? "password" : "text");
	};

	const handleSaveWhatsApp = async (values) => {
		if (!values.email) values.emailPassword = "";

		let canalSelecionado;
		if (!whatsAppId && isHubConection) {
			canalSelecionado = canal.find((item) => String(item.id) === String(values.channel));

			if (!canalSelecionado) {
				toastError("Canal não encontrado");
				return;
			}
		}

		const whatsappData = {
			...values,
			queueIds: selectedQueueIds,
			autoQueueId: selectedAutoQueueId,
			channel: canalSelecionado?.channel ?? values.channel,
		};

		try {
			if (whatsAppId) {
				await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
			} else {
				if (isHubConection) {
					await api.post("/hub-channel/", {
						...whatsappData,
						apikey: canalSelecionado.id,
						channel: canalSelecionado.channel,
						maxAttempts: whatsappData.maxAttempts ?? 3,
						integrationSource: "hub",
					});
				} else {
					await api.post("/whatsapp", whatsappData);
				}
			}
			await reload();
			toast.success(i18n.t("whatsappModal.success"));
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	useEffect(() => {
		if (!open || whatsAppId || !isHubConection) {
			if (!open || whatsAppId) {
				setCanal([]);
			}
			return undefined;
		}

		const getConnectionsHub = async () => {
			try {
				const { data } = await api.get("hub-channel/");
				setCanal(Array.isArray(data) ? data : []);
			} catch (err) {
				toastError(err);
			}
		};

		getConnectionsHub();
	}, [open, isHubConection, whatsAppId]);

	const handleClose = () => {
		onClose();
		setWhatsApp(initialState);
		setSelectedQueueIds([]);
		setSelectedAutoQueueId(null);
		setIsHubConection(false);
		setCanal([]);
		setPasswordType("password");
	};

	const handleCopy = async (text) => {
		try {
			await navigator.clipboard.writeText(text);
			toast.success("Copiado para a área de transferência.");
		} catch {
			toast.error("Não foi possível copiar.");
		}
	};

	const title = whatsAppId
		? i18n.t("whatsappModal.title.edit")
		: i18n.t("whatsappModal.title.add");

	return (
		<Formik
			initialValues={{ ...initialState, ...whatsApp }}
			enableReinitialize
			validationSchema={SessionSchema}
			onSubmit={async (values, actions) => {
				await handleSaveWhatsApp(values);
				actions.setSubmitting(false);
			}}
		>
			{({ values, touched, errors, isSubmitting, handleChange, handleBlur, setFieldValue }) => {
				const webhookUrl = values.apikey
					? `${getBackendUrl()}/webhook/?connectionId=${values.apikey}`
					: "";
				const isEditMode = Boolean(whatsAppId);
				const isCreateWithPreset = !whatsAppId && Boolean(initialChannel);
				const isCreateWithHub = !whatsAppId && Boolean(initialHubMode);
				const showHubToggle = !isEditMode && !isCreateWithPreset && !isCreateWithHub;
				const showChannelSelector = !isEditMode && !isCreateWithPreset;

				return (
					<AppModal
						open={open}
						onClose={handleClose}
						title={title}
						subtitle={
							isEditMode
								? "Ajuste nome, mensagens, setores e credenciais da conexão."
								: undefined
						}
						maxWidth={720}
						footer={
							<>
								<button
									type="button"
									className="btn btn-sub-gray"
									onClick={handleClose}
									disabled={isSubmitting}
								>
									{i18n.t("whatsappModal.buttons.cancel")}
								</button>
								<button
									type="submit"
									form="whatsapp-form"
									className="btn btn-primary"
									disabled={isSubmitting}
								>
									{whatsAppId
										? i18n.t("whatsappModal.buttons.okEdit")
										: i18n.t("whatsappModal.buttons.okAdd")}
								</button>
							</>
						}
					>
						{loading ? (
							<div className="app-modal-loading">
								<div className="app-modal-spinner" />
							</div>
						) : (
							<Form id="whatsapp-form" autoComplete="off" className="space-y-3">
								{isEditMode && (
									<ConnectionChannelBanner
										channel={values.channel}
										hub={values.hub}
									/>
								)}
								<EditSection
									title="Configuração básica"
									description={
										isEditMode
											? "Atualize o nome e se esta conexão será a padrão."
											: "Defina o nome, o comportamento padrão e o canal da conexão."
									}
								>
									<div className="grid w-full gap-4 lg:grid-cols-[minmax(0,1fr)_180px] lg:items-start">
										<div className="min-w-0">
											<label
												htmlFor="whatsapp-name"
												className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100"
											>
												{i18n.t("whatsappModal.form.name")}
											</label>
											<input
												id="whatsapp-name"
												name="name"
												type="text"
												className="form-input w-full"
												value={values.name || ""}
												onChange={handleChange}
												onBlur={handleBlur}
												autoFocus
											/>
											{touched.name && errors.name && (
												<p className="mt-1 text-sm text-danger">{errors.name}</p>
											)}
										</div>

										<ToggleSwitch
											label="Conexão padrão"
											checked={Boolean(values.isDefault)}
											onChange={(e) =>
												setFieldValue("isDefault", e.target.checked)
											}
										/>
									</div>

									{!isEditMode && (
										<div className="mt-4 grid w-full gap-4 lg:grid-cols-[minmax(0,1fr)_180px] lg:items-start">
											<div className="min-w-0">
												{isCreateWithPreset ? (
													<div>
														<p className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100">
															{i18n.t("whatsappModal.form.channel")}
														</p>
														<span className="inline-flex rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
															{getChannelLabel(initialChannel)}
														</span>
													</div>
												) : showChannelSelector ? (
													<>
														<label
															htmlFor="whatsapp-channel"
															className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100"
														>
															{i18n.t("whatsappModal.form.channel")}
														</label>
														<select
															id="whatsapp-channel"
															name="channel"
															className="form-input w-full"
															value={values.channel || (isHubConection ? "" : "whatsapp")}
															onChange={handleChange}
															onBlur={handleBlur}
														>
															{isHubConection || isCreateWithHub ? (
																<>
																	<option value="">Selecione</option>
																	{canal.map((item) => (
																		<option key={item.id} value={item.id}>
																			{formatHubChannelLabel(item)}
																		</option>
																	))}
																</>
															) : (
																CHANNEL_OPTIONS.map((option) => (
																	<option key={option.value} value={option.value}>
																		{option.label}
																	</option>
																))
															)}
														</select>
													</>
												) : null}
											</div>

											{showHubToggle && (
												<ToggleSwitch
													label="Conexao hub"
													tooltip="Ative para usar um canal previamente conectado no hub."
													checked={isHubConection}
													onChange={() => {
														const next = !isHubConection;
														setIsHubConection(next);
														setCanal([]);
														setFieldValue("channel", next ? "" : "whatsapp");
													}}
												/>
											)}
										</div>
									)}

									{!isEditMode && (isHubConection || isCreateWithHub) && canal.length === 0 && (
										<p className="mb-0 mt-3 text-sm text-default-500 dark:text-default-400">
											Nenhum canal do hub encontrado para selecao.
										</p>
									)}
								</EditSection>

								{values.channel === "whatsappcloud" && (
									<EditSection
										title="Credenciais do WhatsApp API"
										description="Preencha os dados do canal oficial da Meta."
									>
										<div className="grid gap-4 md:grid-cols-2">
											<AuthField
												id="waba-phone-number-id"
												label="WABA ID do numero de telefone"
											>
												<input
													id="waba-phone-number-id"
													name="wabaFoneNumberId"
													type="text"
													className="form-input w-full"
													value={values.wabaFoneNumberId || ""}
													onChange={handleChange}
													onBlur={handleBlur}
												/>
											</AuthField>
											<AuthField
												id="waba-account-id"
												label="WABA ID da conta de negocios"
											>
												<input
													id="waba-account-id"
													name="wabaAccountId"
													type="text"
													className="form-input w-full"
													value={values.wabaAccountId || ""}
													onChange={handleChange}
													onBlur={handleBlur}
												/>
											</AuthField>
										</div>
										<AuthField id="waba-access-token" label="WABA chave da API">
											<textarea
												id="waba-access-token"
												name="wabaAccessToken"
												className="form-input min-h-[7rem] w-full resize-y"
												value={values.wabaAccessToken || ""}
												onChange={handleChange}
												onBlur={handleBlur}
												rows={4}
											/>
										</AuthField>
									</EditSection>
								)}

								{values.channel === "facebook" && (
									<EditSection
										title="Credenciais do Facebook"
										description="Informe os dados necessarios para integrar a pagina."
									>
										<div className="grid gap-4 md:grid-cols-2">
											<AuthField id="facebook-page-id" label="Page ID">
												<input
													id="facebook-page-id"
													name="pageId"
													type="text"
													className="form-input w-full"
													value={values.pageId || ""}
													onChange={handleChange}
													onBlur={handleBlur}
												/>
											</AuthField>
											<AuthField id="facebook-page-token" label="Page Access Token">
												<input
													id="facebook-page-token"
													name="pageToken"
													type="text"
													className="form-input w-full"
													value={values.pageToken || ""}
													onChange={handleChange}
													onBlur={handleBlur}
												/>
											</AuthField>
										</div>
									</EditSection>
								)}

								{values.channel === "telegram" && (
									<EditSection
										title="Credenciais do Telegram"
										description="Informe o token do bot para ativar o canal."
									>
										<AuthField id="telegram-bot-token" label="Telegram Bot Token">
											<input
												id="telegram-bot-token"
												name="telegramBotToken"
												type="text"
												className="form-input w-full"
												value={values.telegramBotToken || ""}
												onChange={handleChange}
												onBlur={handleBlur}
											/>
										</AuthField>
									</EditSection>
								)}

								{values.channel === "email" && (
									<EditSection
										title="Configurações de e-mail"
										description="Configure credenciais, servidor de entrada e envio."
									>
										<div className="grid gap-4 md:grid-cols-2">
											<AuthField
												id="email-address"
												label="Endereco de e-mail"
												error={touched.email && errors.email ? "E-mail invalido" : ""}
											>
												<input
													id="email-address"
													name="email"
													type="email"
													className="form-input w-full"
													value={values.email || ""}
													onChange={handleChange}
													onBlur={handleBlur}
												/>
											</AuthField>
											<AuthField id="email-password" label="Senha">
												<div className="relative">
													<input
														id="email-password"
														name="emailPassword"
														type={passwordType}
														className="form-input w-full pr-12"
														value={values.emailPassword || ""}
														onChange={handleChange}
														onBlur={handleBlur}
													/>
													<button
														type="button"
														className="absolute inset-y-0 right-0 flex items-center px-3 text-default-500"
														onClick={handleClickShowPassword}
														aria-label="Mostrar senha"
													>
														{passwordType === "password" ? (
															<Visibility fontSize="small" />
														) : (
															<VisibilityOff fontSize="small" />
														)}
													</button>
												</div>
											</AuthField>
										</div>

										<div className="grid gap-4 md:grid-cols-2">
											<AuthField
												id="email-server"
												label="Host de recebimento de e-mail"
												hint="Ex: smtp.gmail.com. Consulte a documentacao do seu provedor."
											>
												<input
													id="email-server"
													name="emailServer"
													type="text"
													className="form-input w-full"
													value={values.emailServer || ""}
													onChange={handleChange}
													onBlur={handleBlur}
												/>
											</AuthField>
											<AuthField
												id="email-port"
												label="Porta de recebimento de e-mail"
												hint="Costuma ser 993."
											>
												<input
													id="email-port"
													name="emailPort"
													type="number"
													className="form-input w-full"
													value={values.emailPort || ""}
													onChange={handleChange}
													onBlur={handleBlur}
												/>
											</AuthField>
										</div>

										<div className="grid gap-4 md:grid-cols-2">
											<AuthField
												id="email-server-send"
												label="Host de envio de e-mail"
												hint="Pode ser diferente do servidor de recebimento."
											>
												<input
													id="email-server-send"
													name="emailServerSend"
													type="text"
													className="form-input w-full"
													value={values.emailServerSend || ""}
													onChange={handleChange}
													onBlur={handleBlur}
												/>
											</AuthField>
											<AuthField
												id="email-port-send"
												label="Porta de envio de e-mail"
												hint="Costuma ser 465."
											>
												<input
													id="email-port-send"
													name="emailPortSend"
													type="number"
													className="form-input w-full"
													value={values.emailPortSend || ""}
													onChange={handleChange}
													onBlur={handleBlur}
												/>
											</AuthField>
										</div>
									</EditSection>
								)}

								{values.channel !== "email" && (
									<EditSection
										title="Mensagens da conexão"
										description="Defina as mensagens automáticas de saudação e despedida."
									>
										<div className="grid gap-4 lg:grid-cols-2">
											<AuthField
												id="greeting-message"
												label={i18n.t("queueModal.form.greetingMessage")}
												error={touched.greetingMessage && errors.greetingMessage}
											>
												<textarea
													id="greeting-message"
													name="greetingMessage"
													className="form-input min-h-[8rem] w-full resize-y"
													value={values.greetingMessage || ""}
													onChange={handleChange}
													onBlur={handleBlur}
													rows={5}
												/>
											</AuthField>
											<AuthField
												id="farewell-message"
												label={i18n.t("whatsappModal.form.farewellMessage")}
												error={touched.farewellMessage && errors.farewellMessage}
											>
												<textarea
													id="farewell-message"
													name="farewellMessage"
													className="form-input min-h-[8rem] w-full resize-y"
													value={values.farewellMessage || ""}
													onChange={handleChange}
													onBlur={handleBlur}
													rows={5}
												/>
											</AuthField>
										</div>
									</EditSection>
								)}

								<EditSection
									title="Filas e automação"
									description="Escolha as filas disponíveis e o comportamento do chatbot."
								>
									<div className="space-y-4">
										<QueueMultiSelect
											label={i18n.t("queueSelect.inputLabel")}
											options={queues}
											selectedIds={selectedQueueIds}
											onChange={(selectedIds) => setSelectedQueueIds(selectedIds)}
											placeholder="Selecione os setores"
											emptyText="Nenhum setor disponível."
										/>

										<AuthField
											id="auto-queue-id"
											label={i18n.t("autoQueueSelect.inputLabel")}
										>
											<select
												id="auto-queue-id"
												name="autoQueueId"
												className="form-select w-full"
												value={selectedAutoQueueId ?? ""}
												onChange={(e) =>
													setSelectedAutoQueueId(
														e.target.value === "" ? null : e.target.value
													)
												}
											>
												<option value="">{i18n.t("autoQueueSelect.empty")}</option>
												{queues.map((queue) => (
													<option key={queue.id} value={queue.id}>
														{queue.name}
													</option>
												))}
											</select>
										</AuthField>

										<AuthField
											id="max-attempts"
											label={i18n.t("chatbotMaxAttempts")}
										>
											<input
												id="max-attempts"
												name="maxAttempts"
												type="number"
												className="form-input w-full"
												value={values.maxAttempts ?? ""}
												onChange={handleChange}
												onBlur={handleBlur}
											/>
										</AuthField>
									</div>
								</EditSection>

								{(values.createdAt || values.updatedAt) && (
									<EditSection
										title={i18n.t("whatsappModal.form.connectionInfo")}
										description={i18n.t("whatsappModal.form.connectionInfoHint")}
									>
										<div className="grid gap-3 sm:grid-cols-2">
											{values.createdAt && (
												<div className="rounded-lg border border-default-200/80 bg-default-50/60 px-3 py-2.5 dark:border-default-700/80 dark:bg-default-900/40">
													<p className="mb-1 text-xs font-medium text-default-500 dark:text-default-400">
														{i18n.t("whatsappModal.form.createdAt")}
													</p>
													<p className="mb-0 text-sm text-default-800 dark:text-default-100">
														{formatConnectionDateTime(values.createdAt, timeZone)}
													</p>
												</div>
											)}
											{values.updatedAt && (
												<div className="rounded-lg border border-default-200/80 bg-default-50/60 px-3 py-2.5 dark:border-default-700/80 dark:bg-default-900/40">
													<p className="mb-1 text-xs font-medium text-default-500 dark:text-default-400">
														{i18n.t("whatsappModal.form.updatedAt")}
													</p>
													<p className="mb-0 text-sm text-default-800 dark:text-default-100">
														{formatConnectionDateTime(values.updatedAt, timeZone)}
													</p>
												</div>
											)}
										</div>

										{["whatsappcloud", "facebook"].includes(values.channel) &&
											webhookUrl && (
												<div className="mt-3 space-y-1 text-sm text-default-700 dark:text-default-200">
													<div className="flex flex-wrap items-center gap-2">
														<span className="font-medium text-default-900 dark:text-default-50">
															Webhook:
														</span>
														<button
															type="button"
															className="btn btn-sub-gray btn-sm"
															onClick={() => handleCopy(webhookUrl)}
														>
															Copiar
														</button>
													</div>
													<p className="mb-0 break-all text-sm text-default-500 dark:text-default-400">
														{webhookUrl}
													</p>
												</div>
											)}
									</EditSection>
								)}
							</Form>
						)}
					</AppModal>
				);
			}}
		</Formik>
	);
}

export default React.memo(WhatsAppModal);
